import {
  canAddUsersForLicense,
  getFeatureFromLicense,
  hasAssetGroupsInLicense,
  hasValidFeatureInLicense,
  shouldDisplayLicenseWarning,
} from '@/licenseManagement/licenseManagement.utilities';
import {
  publishedLicenseValue,
  setPublishedLicenseConfiguration,
} from '@/licenseManagement/publishedLicenseConfiguration';
import { initializeSeeqAgChartsEnterpriseLicense, initializeSeeqAgGridEnterpriseLicense } from '@seeqdev/ag';
import { type SetStoreState, createStore, createStoreInstance } from '@seeqdev/utils';
import type { Feature } from '@/licenseManagement/licenseManagement.constants';
import type { LicenseData, LicenseManagementState, LicenseStatus } from '@/licenseManagement/licenseManagement.types';

const initialState: LicenseManagementState = Object.freeze({
  licenseStatus: undefined,
  license: {
    additionalFeatures: [],
  },
  showOfficeHours: false,
});

type LicenseManagementActions = ReturnType<typeof licenseManagementActionsFactory>;

const licenseManagementActionsFactory = (set: SetStoreState<LicenseManagementState>) => ({
  setLicense: (license: LicenseData) => {
    setPublishedLicenseConfiguration(license.configuration);
    // AG uses global license managers; configure both before subscribers receive the new license.
    const agLicense = publishedLicenseValue('AG Grid and Charts', 'License Key') || undefined;
    initializeSeeqAgChartsEnterpriseLicense(agLicense);
    initializeSeeqAgGridEnterpriseLicense(agLicense);
    set((draft) => {
      draft.license = license;
    });
  },
  setLicenseStatus: (status: LicenseStatus) =>
    set((draft) => {
      draft.licenseStatus = status;
    }),
  setShowOfficeHours: (show: boolean) =>
    set((draft) => {
      draft.showOfficeHours = show;
    }),
  reset: () => {
    setPublishedLicenseConfiguration(undefined);
    initializeSeeqAgChartsEnterpriseLicense(undefined);
    initializeSeeqAgGridEnterpriseLicense(undefined);
    set(() => initialState);
  },
});

const STORE_NAME = 'LicenseManagement';

const [LicenseManagementStoreProvider, useLicenseManagementStoreInstance, useLicenseManagementSelector] = createStore(
  STORE_NAME,
  initialState,
  licenseManagementActionsFactory,
);

export { LicenseManagementStoreProvider };
export const useLicenseStatus = () => useLicenseManagementSelector((state) => state.licenseStatus);
export const useLicense = () => useLicenseManagementSelector((state) => state.license);
export const useContractNumber = () => useLicenseManagementSelector((state) => state.license.contractNumber);
export const useLicenseConfiguration = () => useLicenseManagementSelector((state) => state.license?.configuration);
export const useShowOfficeHours = () => useLicenseManagementSelector((state) => state.showOfficeHours);
export const useIsTrial = () => useLicenseManagementSelector((state) => state.license?.level === 'Trial');
export const useLicenseManagementActions = () => useLicenseManagementStoreInstance().actions;

export const useDisplayLicenseWarning = () =>
  useLicenseManagementSelector((state) => shouldDisplayLicenseWarning(state.licenseStatus));

export const useCanAddUsers = () => useLicenseManagementSelector((state) => canAddUsersForLicense(state.license));

export const useHasValidFeature = (feature: Feature) =>
  useLicenseManagementSelector((state) => hasValidFeatureInLicense(state.license, feature));

export const useHasAssetGroups = () => useLicenseManagementSelector((state) => hasAssetGroupsInLicense(state.license));

export const useGetFeature = (name: string) =>
  useLicenseManagementSelector((state) => getFeatureFromLicense(state.license, name));

/**
 * @deprecated Test-only helper. Use test utils instead.
 *
 * Creates a test-specific license management store instance.
 */
export function createTestLicenseManagementStore(overrides?: Partial<LicenseManagementState>) {
  if (process.env.NODE_ENV !== 'test') {
    throw new Error('createTestLicenseManagementStore() should only be used in tests.');
  }

  return createStoreInstance({ ...initialState, ...overrides }, licenseManagementActionsFactory);
}
/**
 * @deprecated This function is ONLY for legacy migration from Flux/Baobab stores.
 * DO NOT use in new code.
 */
export function legacyCreateLicenseManagementStore() {
  return createStoreInstance<LicenseManagementState, LicenseManagementActions>(
    initialState,
    licenseManagementActionsFactory,
    { storeName: STORE_NAME },
  );
}
